import { useDataTableFeatures } from "@/components/data-table/hooks/useDataTableFeatures";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import useTableColumnVisibility from "@/components/data-table/hooks/useTableColumnVisibility";
import SearchInput from "@/components/SearchInput";
import {
  ResponsiveMenu,
  ResponsiveMenuCheckboxItem,
  ResponsiveMenuItem,
  ResponsiveMenuSeparator,
} from "@/components/ui/responsive-menu";
import { Columns3, RotateCcw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface QueryTableColumnsMenuBarMenuProps {
  disabled?: boolean;
  inDialog?: boolean;
}

function QueryTableColumnsMenuBarMenu<TData>({
  disabled,
  inDialog,
}: QueryTableColumnsMenuBarMenuProps) {
  const { t } = useTranslation();
  const { table } = useDataTableSettings<TData>();
  const { columnVisibility, resetColumnVisibility } =
    useTableColumnVisibility();

  const onResetColumnVisibility = useCallback(() => {
    resetColumnVisibility();
  }, [resetColumnVisibility]);

  const { enableColumnVisibility } = useDataTableFeatures();
  const [search, setSearch] = useState("");

  const onSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [],
  );

  const onSearchKeyDown = useCallback((event: React.KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  const hidableColumns = useMemo(
    () => table.getAllColumns().filter((column) => column.getCanHide()),
    [table],
  );

  const filteredColumns = useMemo(
    () =>
      hidableColumns.filter((column) => {
        if (!search) {
          return true;
        }
        const label = t(column.columnDef.header as string);
        return label.toLowerCase().includes(search.toLowerCase());
      }),
    [hidableColumns, search, t],
  );

  if (!enableColumnVisibility) return null;

  return (
    <ResponsiveMenu
      label={t("listMenubar.columns.trigger")}
      icon={<Columns3 className="h-5 w-5" />}
      disabled={disabled}
      contentClassName={inDialog ? "pointer-events-auto" : ""}
      onClose={() => setSearch("")}
    >
      <header onKeyDown={onSearchKeyDown} className="p-2">
        <SearchInput
          autoFocus
          value={search}
          onChange={onSearchChange}
          placeholder={t("common.search")}
        />
      </header>
      <div className="max-h-[40dvh] overflow-y-auto">
        {filteredColumns.length === 0 ? (
          <p className="text-muted-foreground p-2 text-center text-sm">
            {t("common.noResults")}
          </p>
        ) : (
          filteredColumns.map((column) => {
            return (
              <ResponsiveMenuCheckboxItem
                key={column.id}
                className="capitalize"
                checked={columnVisibility[column.id]}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
                onSelect={(event) => event.preventDefault()}
              >
                {t(column.columnDef.header as string)}
              </ResponsiveMenuCheckboxItem>
            );
          })
        )}
      </div>
      <ResponsiveMenuSeparator />
      <ResponsiveMenuItem onClick={onResetColumnVisibility}>
        <RotateCcw className="mr-2 w-4" />
        {t("listMenubar.columns.resetVisibility")}
      </ResponsiveMenuItem>
    </ResponsiveMenu>
  );
}

export default QueryTableColumnsMenuBarMenu;
